import { useState, useEffect } from "react";
import { useStore } from "@/store/useStore";
import { Metal, Purity, Unit, ValuationResult, calculateMetalValue, GoldPurity, SilverPurity } from "@/lib/pricing";
import { toast } from "sonner";
import { GOLD_PURITIES, SILVER_PURITIES } from "@/lib/constants";

interface UseValuationCalculationProps {
  selectedMetal: Metal;
  goldPurity: GoldPurity;
  silverPurity: SilverPurity;
  weight: string;
  unit: Unit;
}

export function useValuationCalculation({
  selectedMetal,
  goldPurity,
  silverPurity,
  weight,
  unit
}: UseValuationCalculationProps) {
  const [result, setResult] = useState<ValuationResult | null>(null);
  const [errorState, setErrorState] = useState<string | null>(null);

  const {
    currentGoldPrice: rawGoldPrice,
    currentSilverPrice: rawSilverPrice,
    fetchCurrentPricesData,
  } = useStore();

  // Ensure we have real prices on component mount
  useEffect(() => {
    const loadPrices = async () => {
      try {
        if (!rawGoldPrice || !rawSilverPrice) {
          console.log("ValuationModal: Fetching fresh prices from store action");
          await fetchCurrentPricesData();
        }
      } catch (error) {
        console.error("Error refreshing prices:", error);
        setErrorState("Impossibile caricare i prezzi attuali. Riprova più tardi.");
        toast.error("Impossibile caricare i prezzi attuali");
      }
    };
    
    loadPrices();
  }, [fetchCurrentPricesData, rawGoldPrice, rawSilverPrice]);

  // Calculation effect
  useEffect(() => {
    try {
      const goldPrice = rawGoldPrice?.price || 0;
      const silverPrice = rawSilverPrice?.price || 0;

      console.log("Calculation with:", {
        metal: selectedMetal,
        goldPurity,
        silverPurity,
        selectedPurity: selectedMetal === "ORO" ? goldPurity : silverPurity,
        currentPrice: selectedMetal === "ORO" ? goldPrice : silverPrice
      });
      
      // Reset error state if prices are available
      if ((selectedMetal === "ORO" && goldPrice > 0) || 
          (selectedMetal === "ARGENTO" && silverPrice > 0)) {
        setErrorState(null);
      } else {
        if (!errorState) {
          setErrorState("Prezzi non disponibili. Riprova dopo aver aggiornato i prezzi.");
        }
        setResult(null);
        return;
      }
      
      const weightValue = parseFloat(weight) || 0;
      
      // Get the raw base price from the store
      let rawBasePrice = 0;
      if (selectedMetal === "ORO" && rawGoldPrice) {
        rawBasePrice = rawGoldPrice.price || 0;
      } else if (selectedMetal === "ARGENTO" && rawSilverPrice) {
        rawBasePrice = rawSilverPrice.price || 0;
      }

      // Use the raw base price for calculation
      const selectedPurity = selectedMetal === "ORO" ? goldPurity : silverPurity;
      
      const valuationResult = calculateMetalValue(
        rawBasePrice,
        selectedMetal,
        selectedPurity,
        weightValue,
        unit
      );
      
      setResult(valuationResult);
    } catch (error) {
      console.error("Error calculating value:", error);
      setErrorState("Errore nel calcolo del valore");
      setResult(null);
    }
  }, [selectedMetal, goldPurity, silverPurity, weight, unit, rawGoldPrice, rawSilverPrice, errorState]);

  const handleRefreshPrices = async () => {
    try {
      setErrorState(null);
      await fetchCurrentPricesData();
      toast.success("Prezzi aggiornati dal database");
    } catch (error) {
      console.error("Error refreshing prices:", error);
      setErrorState("Impossibile aggiornare i prezzi");
      toast.error("Impossibile aggiornare i prezzi");
    }
  };

  return {
    result,
    errorState,
    handleRefreshPrices
  };
}
